import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { AgGridReact } from "ag-grid-react";
import type { CellFocusedEvent, ColDef } from "ag-grid-community";
import type { Deal } from "@/types/deal";
import { buildPipelineColumnDefs } from "@/pages/pipeline/pipelineColumnDefs";
import {
  applyPipelineView,
  PIPELINE_VIEWS,
  type PipelineViewId,
} from "./pipelineViews";

type PipelineLists = Parameters<typeof buildPipelineColumnDefs>[0]["lists"];

type UsePipelineGridArgs = {
  /** Cells are editable (the user may edit and the grid is not in view-only mode). */
  editable: boolean;
  view: PipelineViewId;
  customColumns: string[];
  lists: PipelineLists;
  setRightSideBarOpen: (open: boolean) => void;
};

export function getDealIdFromRow(data: any): number | null {
  if (!data) return null;
  const isAccordion =
    data && "parent_deal_id" in data && data.parent_deal_id != null;
  const id = isAccordion ? (data.parent_deal_id ?? data.deal_id) : data.id;
  return typeof id === "number" && id > 0 ? id : null;
}

export function usePipelineGrid({
  editable,
  view,
  customColumns,
  lists,
  setRightSideBarOpen,
}: UsePipelineGridArgs) {
  const gridRef = useRef<AgGridReact<Deal>>(null);
  const [selectedDealId, setSelectedDealId] = useState<number | null>(null);

  const baseColumnDefs: ColDef[] = useMemo(
    () =>
      buildPipelineColumnDefs({
        editMode: editable,
        lists,
      }),
    [lists, editable],
  );

  const columnDefs: ColDef[] = useMemo(
    () => applyPipelineView(baseColumnDefs, view, customColumns),
    [baseColumnDefs, view, customColumns],
  );

  // Stacked Meeting columns need (compact) two-line rows.
  const rowHeight = PIPELINE_VIEWS[view].tallRows ? 34 : 27;

  // Clicking a row selects its deal and opens the notes/tasks panel for it.
  const onRowClicked = useCallback(
    (event: any) => {
      if (!event.data) return;
      const dealId = getDealIdFromRow(event.data);
      if (dealId == null) return;
      setSelectedDealId(dealId);
      setRightSideBarOpen(true);
    },
    [setRightSideBarOpen],
  );

  // Moving the focused cell (arrow keys, Tab, clicks) selects that row's deal,
  // so the selected deal and an open notes panel follow the keyboard.
  const onCellFocused = useCallback((event: CellFocusedEvent) => {
    if (event.rowIndex == null || event.rowPinned) return;
    const rowNode = event.api.getDisplayedRowAtIndex(event.rowIndex);
    const dealId = getDealIdFromRow(rowNode?.data);
    if (dealId != null) setSelectedDealId(dealId);
  }, []);

  const getRowClass = useCallback(
    (params: { data?: unknown }) => {
      if (selectedDealId == null) return undefined;
      const dealId = getDealIdFromRow(params.data);
      return dealId === selectedDealId
        ? "pipeline-deals-grid-row--selected"
        : undefined;
    },
    [selectedDealId],
  );

  useEffect(() => {
    const api = gridRef.current?.api;
    const gridEl = document.querySelector(".pipeline-deals-grid");
    if (!api || !gridEl) return;

    const selectedRowIds = new Set<string>();
    api.forEachNode((node) => {
      if (getDealIdFromRow(node.data) === selectedDealId && node.id != null) {
        selectedRowIds.add(String(node.id));
      }
    });

    gridEl.querySelectorAll(".ag-row").forEach((row) => {
      const rowId = row.getAttribute("row-id");
      if (rowId && selectedRowIds.has(rowId)) {
        row.classList.add("pipeline-deals-grid-row--selected");
      } else {
        row.classList.remove("pipeline-deals-grid-row--selected");
      }
    });
  }, [selectedDealId]);

  return {
    gridRef,
    selectedDealId,
    setSelectedDealId,
    baseColumnDefs,
    columnDefs,
    rowHeight,
    onRowClicked,
    onCellFocused,
    getRowClass,
  };
}
